import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import api, { apiError, fmtNum, fmtMoney } from "@/lib/api";
import { PageHeader, Card, StatusBadge, CrudActions } from "@/components/kit";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { InwardEditDialog, OutwardEditDialog } from "@/components/TxnEditDialog";
import { Loader2 } from "lucide-react";

function useList(url, key) {
  return useQuery({ queryKey: [key], queryFn: async () => (await api.get(url)).data });
}

function TableWrap({ cols, rows, render, testid, empty = "No records" }) {
  return (
    <Card className="overflow-hidden" data-testid={testid}>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-stone-50 text-left text-[11px] uppercase tracking-wide text-stone-500">
            <tr>{cols.map((c) => <th key={c.k} className={`px-4 py-3 ${c.right ? "text-right" : ""}`}>{c.t}</th>)}</tr>
          </thead>
          <tbody>
            {rows.length === 0 && <tr><td colSpan={cols.length} className="px-4 py-8 text-center text-stone-400">{empty}</td></tr>}
            {rows.map((r, i) => <tr key={r.id || i} className="border-t border-stone-100 even:bg-stone-50/50">{render(r)}</tr>)}
          </tbody>
        </table>
      </div>
    </Card>
  );
}

export default function Registers() {
  const qc = useQueryClient();
  const inward = useList("/txn/inward", "reg-inward");
  const outward = useList("/txn/outward", "reg-outward");
  const ledger = useList("/reports/stock-ledger", "reg-ledger");
  const labour = useList("/reports/labour-ledger", "reg-labour");
  const [editingIn, setEditingIn] = useState(null);
  const [editingOut, setEditingOut] = useState(null);

  const refresh = () => {
    qc.invalidateQueries({ queryKey: ["reg-inward"] });
    qc.invalidateQueries({ queryKey: ["reg-outward"] });
    qc.invalidateQueries({ queryKey: ["reg-ledger"] });
    qc.invalidateQueries({ queryKey: ["import-posted"] });
    qc.invalidateQueries({ queryKey: ["txn"] });
  };

  const remove = async (kind, row) => {
    const num = row.inward_number || row.outward_number;
    if (!window.confirm(`Delete ${num}? Stock bags/weight will be adjusted through the lot ledger.`)) return;
    try {
      await api.delete(`/txn/${kind}/${row.id}`);
      toast.success(`${num} deleted`);
      refresh();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const loading = inward.isLoading || outward.isLoading;
  if (loading) return <div className="grid place-items-center py-24"><Loader2 className="animate-spin text-emerald-700" size={32} /></div>;

  return (
    <div data-testid="registers-page">
      <PageHeader title="Registers & Ledgers" subtitle="Transaction registers with full source traceability — edit or delete any row" />
      <Tabs defaultValue="inward">
        <TabsList className="mb-4">
          <TabsTrigger value="inward" data-testid="tab-inward">Inward</TabsTrigger>
          <TabsTrigger value="outward" data-testid="tab-outward">Outward</TabsTrigger>
          <TabsTrigger value="ledger" data-testid="tab-ledger">Stock Ledger</TabsTrigger>
          <TabsTrigger value="labour" data-testid="tab-labour">Labour & Dala</TabsTrigger>
        </TabsList>

        <TabsContent value="inward">
          <TableWrap testid="inward-register" rows={inward.data || []}
            cols={[
              { k: "n", t: "Inward No" }, { k: "d", t: "Date" }, { k: "src", t: "Source" },
              { k: "o", t: "Owner" }, { k: "s", t: "Sender" }, { k: "i", t: "Item" },
              { k: "l", t: "Lot" }, { k: "b", t: "Bags", right: true }, { k: "w", t: "Net Qtl", right: true },
              { k: "a", t: "Actions", right: true },
            ]}
            render={(r) => (<>
              <td className="px-4 py-3 font-mono text-xs font-semibold text-emerald-700">{r.inward_number}</td>
              <td className="px-4 py-3">{r.date}</td>
              <td className="px-4 py-3 text-xs uppercase text-stone-500">{r.source_type || "—"}</td>
              <td className="px-4 py-3">{r.stock_owner_name}</td>
              <td className="px-4 py-3 text-stone-500">{r.sender_name || "—"}</td>
              <td className="px-4 py-3">{r.item_name}</td>
              <td className="px-4 py-3 font-mono text-xs">{r.lot_number}</td>
              <td className="px-4 py-3 text-right font-semibold">{fmtNum(r.bags)}</td>
              <td className="px-4 py-3 text-right">{fmtNum(r.net_weight)}</td>
              <td className="px-4 py-3">
                <CrudActions testid={`reg-in-${r.id}`} onEdit={() => setEditingIn(r)} onDelete={() => remove("inward", r)} />
              </td>
            </>)} />
        </TabsContent>

        <TabsContent value="outward">
          <TableWrap testid="outward-register" rows={outward.data || []}
            cols={[
              { k: "n", t: "Outward No" }, { k: "d", t: "Date" }, { k: "src", t: "Source" },
              { k: "o", t: "Owner" }, { k: "r", t: "Receiver" }, { k: "i", t: "Item" },
              { k: "l", t: "Lot" }, { k: "b", t: "Bags", right: true }, { k: "w", t: "Net Qtl", right: true },
              { k: "a", t: "Actions", right: true },
            ]}
            render={(r) => (<>
              <td className="px-4 py-3 font-mono text-xs font-semibold text-emerald-700">{r.outward_number}</td>
              <td className="px-4 py-3">{r.date}</td>
              <td className="px-4 py-3 text-xs uppercase text-stone-500">{r.source_type || "—"}</td>
              <td className="px-4 py-3">{r.stock_owner_name}</td>
              <td className="px-4 py-3 text-stone-500">{r.receiver_name || "—"}</td>
              <td className="px-4 py-3">{r.item_name}</td>
              <td className="px-4 py-3 font-mono text-xs">{r.lot_number}</td>
              <td className="px-4 py-3 text-right font-semibold">{fmtNum(r.bags)}</td>
              <td className="px-4 py-3 text-right">{fmtNum(r.net_weight)}</td>
              <td className="px-4 py-3">
                <CrudActions testid={`reg-out-${r.id}`} onEdit={() => setEditingOut(r)} onDelete={() => remove("outward", r)} />
              </td>
            </>)} empty="No outward dispatches yet" />
        </TabsContent>

        <TabsContent value="ledger">
          <TableWrap testid="stock-ledger-table" rows={ledger.data || []}
            cols={[{ k: "t", t: "Type" }, { k: "n", t: "Ref" }, { k: "l", t: "Lot" }, { k: "p", t: "Party" }, { k: "in", t: "In", right: true }, { k: "out", t: "Out", right: true }, { k: "bal", t: "Balance", right: true }]}
            render={(r) => (<>
              <td className="px-4 py-3"><StatusBadge tone={r.txn_type === "INWARD" ? "emerald" : "info"}>{r.txn_type}</StatusBadge></td>
              <td className="px-4 py-3 font-mono text-xs">{r.txn_number}</td>
              <td className="px-4 py-3 font-mono text-xs">{r.lot_number}</td>
              <td className="px-4 py-3">{r.party}</td>
              <td className="px-4 py-3 text-right text-emerald-700">{r.bags_in ? fmtNum(r.bags_in) : "—"}</td>
              <td className="px-4 py-3 text-right text-red-600">{r.bags_out ? fmtNum(r.bags_out) : "—"}</td>
              <td className="px-4 py-3 text-right font-semibold">{fmtNum(r.balance_bags)}</td>
            </>)} />
        </TabsContent>

        <TabsContent value="labour">
          <TableWrap testid="labour-register" rows={labour.data || []}
            cols={[{ k: "n", t: "Ref" }, { k: "c", t: "Contractor" }, { k: "w", t: "Work" }, { k: "q", t: "Bags", right: true }, { k: "wp", t: "Work Pay", right: true }, { k: "dala", t: "Dala", right: true }, { k: "short", t: "Shortfall", right: true }, { k: "rec", t: "Recover From" }, { k: "st", t: "Status" }]}
            render={(r) => (<>
              <td className="px-4 py-3 font-mono text-xs">{r.source_txn_number}</td>
              <td className="px-4 py-3">{r.contractor_name || "—"}</td>
              <td className="px-4 py-3">{r.work_type}</td>
              <td className="px-4 py-3 text-right">{fmtNum(r.quantity)}</td>
              <td className="px-4 py-3 text-right font-semibold">{fmtMoney(r.work_payment)}</td>
              <td className="px-4 py-3 text-right">{fmtMoney(r.total_dala_to_labour)}</td>
              <td className="px-4 py-3 text-right text-amber-700">{fmtMoney(r.dala_shortfall)}</td>
              <td className="px-4 py-3 text-stone-500">{r.recovery_party_name || "—"}</td>
              <td className="px-4 py-3"><StatusBadge tone={r.dala_recovery_status === "Pending" ? "warning" : "neutral"}>{r.dala_recovery_status}</StatusBadge></td>
            </>)} />
        </TabsContent>
      </Tabs>

      <InwardEditDialog
        record={editingIn}
        onClose={() => setEditingIn(null)}
        onSaved={() => { setEditingIn(null); refresh(); }}
      />
      <OutwardEditDialog
        record={editingOut}
        onClose={() => setEditingOut(null)}
        onSaved={() => { setEditingOut(null); refresh(); }}
      />
    </div>
  );
}
